import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useGameStore from '@store/useGameStore';
import useAuthStore from '@store/useAuthStore';
import useCinematicStore from '@store/useCinematicStore';
import { GameStatus, PlayerStatusInGame } from '@utils/enums';
import { finalizePlanningApi, finalizeDeploymentApi, continueToNextTurnApi } from '@api/gameApi';
import { EMPTY_ARRAY } from '@utils/constants';
import feedback from '@/components/ui/general/feedback/feedback';
import { useT } from '@/i18n';
import './GameStatusButtonPanel.css';

/**
 * Dynamic Game Status Button Panel
 * Shows appropriate action buttons based on current game phase
 * Centralizes all game action buttons in one place
 */
export default function GameStatusButtonPanel() {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const t = useT();

  // Get data from stores
  const game = useGameStore((state) => state.game);
  const user = useAuthStore((state) => state.user);
  
  // Derive current player and status
  const players = useMemo(() => game?.players || EMPTY_ARRAY, [game]);
  const currentPlayer = players.find((p) => p.user_id === user?.id) ?? null;
  const playerStatus = currentPlayer?.pivot?.status_in_game;
  const gameStatus = game?.status;
  
  // Get units using the getter with getState() to avoid subscription loops
  const hasDeployedUnits = useMemo(() => {
    if (!currentPlayer) return false;
    const units = useGameStore.getState().getCurrentUnits();
    const deployedUnits = units.filter(u => u.player_id === currentPlayer.id);
    return deployedUnits.length > 0;
  }, [currentPlayer, gameStatus]); // Re-calculate when player or game status changes
  
  // Check if player has planned actions (for planning phase)
  const hasPlannedActions = useMemo(() => {
    if (!currentPlayer) return false;
    // Only check planned actions during planning phase
    if (gameStatus !== GameStatus.PLAN) return false;

    const plannedActions = useGameStore.getState().getPlannedActions();
    const playerActions = plannedActions.filter(a => a.player_id === currentPlayer.id);
    return playerActions.length > 0;
  }, [currentPlayer, gameStatus]); // Re-calculate when player or game status changes

  // Check if ALL player units have at least 1 planned action
  const allUnitsHaveActions = useMemo(() => {
    if (!currentPlayer || gameStatus !== GameStatus.PLAN) return false;

    const units = useGameStore.getState().getCurrentUnits();
    const playerUnits = units.filter(u => u.player_id === currentPlayer.id && u.current_hp > 0);

    if (playerUnits.length === 0) return false;

    const plannedActions = useGameStore.getState().getPlannedActions();
    const playerActions = plannedActions.filter(a => a.player_id === currentPlayer.id);

    // Debug: Show which units have/don't have actions
    const unitsWithActions = playerUnits.filter(unit =>
      playerActions.some(action => action.unit_id === unit.id)
    );
    const unitsWithoutActions = playerUnits.filter(unit =>
      !playerActions.some(action => action.unit_id === unit.id)
    );

    console.log('[ButtonAttention] Units status:', {
      total: playerUnits.length,
      withActions: unitsWithActions.map(u => u.unit_template?.name || u.id),
      missingActions: unitsWithoutActions.map(u => u.unit_template?.name || u.id),
      allReady: unitsWithoutActions.length === 0
    });

    // Each unit must have at least 1 action
    return unitsWithoutActions.length === 0;
  }, [currentPlayer, gameStatus, game?.team_planned_actions]);

  // Detect if cinematic is complete (for RESOLVED phase attention animation)
  const executedResolutionQueueIndex = useCinematicStore((state) => state.executedResolutionQueueIndex);
  const resolutionQueueLength = game?.resolution_queue?.length || 0;
  const isCinematicComplete = resolutionQueueLength > 0 &&
    executedResolutionQueueIndex >= resolutionQueueLength - 1;

  // State for delayed attention animation after cinematic completes
  const [showNextTurnAttention, setShowNextTurnAttention] = useState(false);

  useEffect(() => {
    if (isCinematicComplete && gameStatus === GameStatus.RESOLVED) {
      const timer = setTimeout(() => setShowNextTurnAttention(true), 1000);
      return () => clearTimeout(timer);
    } else {
      setShowNextTurnAttention(false);
    }
  }, [isCinematicComplete, gameStatus]);

  // Button handlers
  const handleFinalizeDeployment = async () => {
    if (!game || isLoading) return;
    
    setIsLoading(true);
    try {
      await finalizeDeploymentApi(game.id);
    } catch {
      // Error logging and feedback handled centrally by api.js
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFinalizePlanning = async () => {
    if (!game || isLoading) return;
    
    setIsLoading(true);
    try {
      await finalizePlanningApi(game.id);
    } catch {
      // Error logging and feedback handled centrally by api.js
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleContinueAfterResolution = async () => {
    if (!game?.id) return;

    setIsLoading(true);
    try {
      const response = await continueToNextTurnApi(game.id);

      // Check if we're waiting for other players
      const userMessage = response?.userMessage || '';
      if (userMessage.includes('Esperando')) {
        feedback.info(userMessage);
      }
      // Game will be updated through the API interceptor
    } catch (error) {
      console.error('[GameStatusButtonPanel] Failed to continue to next turn:', error);
      feedback.error(t('game_status.error_continue'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Determine button configuration based on game status
  const getButtonConfig = () => {
    if (!game || !currentPlayer) return null;

    switch (gameStatus) {
      case GameStatus.DEPLOY:
        return {
          text: playerStatus === PlayerStatusInGame.DEPLOYED
            ? t('game_status.waiting_others')
            : hasDeployedUnits
              ? t('game_status.finalize_deployment')
              : t('game_status.deploy_first'),
          onClick: handleFinalizeDeployment,
          disabled: isLoading ||
                   playerStatus === PlayerStatusInGame.DEPLOYED ||
                   !hasDeployedUnits,
          variant: 'primary'
        };

      case GameStatus.PLAN:
        return {
          text: playerStatus === PlayerStatusInGame.PLANNED
            ? t('game_status.waiting_others')
            : t('game_status.finalize_planning'),
          onClick: handleFinalizePlanning,
          disabled: isLoading || playerStatus === PlayerStatusInGame.PLANNED,
          variant: 'primary'
        };

      case GameStatus.RESOLVED:
        return {
          text: t('game_status.continue_next_turn'),
          onClick: handleContinueAfterResolution,
          disabled: isLoading,
          variant: 'success'
        };

      case GameStatus.RESOLVING:
        return {
          text: t('game_status.resolving'),
          onClick: () => {},
          disabled: true,
          variant: 'secondary'
        };

      case GameStatus.FINISH:
        return {
          text: t('game_status.return_dashboard'),
          onClick: () => navigate('/dashboard'),
          disabled: false,
          variant: 'primary'
        };

      default:
        return null;
    }
  };
  
  const buttonConfig = getButtonConfig();
  
  // Don't render if no button needed for current phase
  if (!buttonConfig) return null;
  
  // Determine if button should show attention animation
  const shouldShowAttention =
    (gameStatus === GameStatus.RESOLVED && showNextTurnAttention) ||
    (gameStatus === GameStatus.PLAN && allUnitsHaveActions && !buttonConfig.disabled);

  return (
    <div className="game-status-button-panel">
      <button
        className={`button button-cta button-cta-combo button-enhanced button-spaced button-${buttonConfig.variant}${shouldShowAttention ? ' button-attention' : ''}`}
        onClick={buttonConfig.onClick}
        disabled={buttonConfig.disabled}
      >
        {isLoading ? (
          <span className="loading-spinner">{t('game_status.processing')}</span>
        ) : (
          buttonConfig.text
        )}
      </button>
      
      {/* Optional: Add helper text based on status */}
      {gameStatus === GameStatus.DEPLOY && !hasDeployedUnits && (
        <p className="helper-text">
          {t('game_status.helper_deploy')}
        </p>
      )}

      {gameStatus === GameStatus.PLAN && !hasPlannedActions && (
        <p className="helper-text">
          {t('game_status.helper_plan')}
        </p>
      )}
    </div>
  );
}